import { BrowserRouter, useRoutes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { PrivacyProvider } from '@/contexts/PrivacyContext';
import { GoApiProvider } from '@/contexts/GoApiContext';
import { InstallPromptProvider } from '@/contexts/InstallPromptContext';
import { CurrencyTransitionProvider } from '@/contexts/CurrencyTransitionContext';
import { CurrencyPrecisionProvider } from '@/contexts/CurrencyPrecisionContext';
import { SessionExpiredProvider } from '@/contexts/SessionExpiredContext';
import { OfflineNotification } from '@/components/OfflineNotification';
import { SessionExpiredOverlay } from '@/components/SessionExpiredOverlay';
import { InsufficientCreditsDialog } from '@/components/InsufficientCreditsDialog';
import { GlobalLoadingIndicator } from '@/components/GlobalLoadingIndicator';
import { PostHogTracker } from '@/components/PostHogTracker';
import { RouteMeta } from '@/components/RouteMeta';
import { appRoutes } from '@/routes/appRoutes';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

/**
 * Renders the route tree from routes/appRoutes.tsx.
 *
 * `useRoutes` needs a router above it, so this sits inside BrowserRouter as its
 * own component rather than in App directly.
 */
function AppRoutes() {
    return useRoutes(appRoutes);
}

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ThemeProvider>
                    <LayoutProvider>
                        <PrivacyProvider>
                            <GoApiProvider>
                            <InstallPromptProvider>
                            <SessionExpiredProvider>
                            <AuthProvider>
                            <CurrencyPrecisionProvider>
                            <CurrencyTransitionProvider>
                                <Toaster richColors position="bottom-right" closeButton />
                                <PostHogTracker />
                                <RouteMeta />
                                <GlobalLoadingIndicator />
                                <OfflineNotification />
                                <SessionExpiredOverlay />
                                <InsufficientCreditsDialog />
                                <AppRoutes />
                            </CurrencyTransitionProvider>
                            </CurrencyPrecisionProvider>
                            </AuthProvider>
                            </SessionExpiredProvider>
                            </InstallPromptProvider>
                            </GoApiProvider>
                        </PrivacyProvider>
                    </LayoutProvider>
                </ThemeProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}
